import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowUpRight, Check, ChevronDown, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

import { CONTACT, STUDIO_INFO } from '@/constants/studio'

type Status = 'idle' | 'sending' | 'sent' | 'error'

interface Values {
    name: string
    email: string
    subject: string
    budget: string
    message: string
    company: string
}

const EMPTY: Values = {
    name: '',
    email: '',
    subject: CONTACT.subjects[0],
    budget: '',
    message: '',
    /* honeypot — see the API route */
    company: ''
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const FIELD =
    'w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-3 text-[14px] text-[#F4F1EA] outline-none transition-colors duration-200 placeholder:text-[#F4F1EA]/30 focus:border-[#4EA3FF]/70 focus:bg-white/[0.07] disabled:opacity-50'

const LABEL = 'block text-[10px] font-medium uppercase tracking-[0.28em] text-[#F4F1EA]/45'

const validate = (values: Values): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (values.name.trim().length < 2) errors.name = 'Please tell us who you are.'
    if (!EMAIL.test(values.email.trim())) errors.email = 'That email does not look right.'
    if (values.message.trim().length < 10) errors.message = 'A sentence or two is enough, but we need one.'

    return errors
}

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(EMPTY)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [notice, setNotice] = useState('')
    const [sentName, setSentName] = useState('')

    const sending = status === 'sending'

    const update =
        (field: keyof Values) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
            const next = event.target.value

            setValues((current) => ({ ...current, [field]: next }))
            setStatus((current) => (current === 'error' ? 'idle' : current))
            setNotice('')
            setErrors((current) => {
                if (!current[field]) return current

                const rest = { ...current }
                delete rest[field]

                return rest
            })
        }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const found = validate(values)

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('error')
            setNotice('Some fields need another look.')

            return
        }

        setStatus('sending')
        setErrors({})
        setNotice('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const payload = await response.json().catch(() => null)

            if (!response.ok || !payload?.ok) {
                setErrors(payload?.errors ?? {})
                setStatus('error')
                setNotice(payload?.message ?? 'The message could not be sent. Please try again.')

                return
            }

            setSentName(values.name.trim().split(' ')[0])
            setValues(EMPTY)
            setStatus('sent')
            setNotice('')
        } catch {
            setStatus('error')
            setNotice('The message could not be sent. Please check your connection and try again.')
        }
    }

    if (status === 'sent') {
        return (
            <div
                className='flex min-h-[420px] flex-col justify-center rounded-lg border border-white/10 bg-white/[0.03] p-8 text-center sm:p-12 animate-in fade-in zoom-in-95 duration-500 ease-out motion-reduce:animate-none'
                role='status'
            >
                <span className='mx-auto grid size-11 place-items-center rounded-full bg-[#4EA3FF]'>
                    <Check className='size-5 text-white' aria-hidden />
                </span>

                <h3 className='mt-7 font-heading text-[clamp(28px,3.4vw,40px)] leading-none'>
                    {CONTACT.success.title}
                    {sentName && `, ${sentName}`}
                </h3>

                <p className='mx-auto mt-5 max-w-[42ch] text-[14px] leading-[1.75] text-[#F4F1EA]/65'>
                    {CONTACT.success.body}
                </p>

                <button
                    type='button'
                    onClick={() => {
                        setStatus('idle')
                        setSentName('')
                    }}
                    className='mx-auto mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-white/40 hover:bg-white/[0.08] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                >
                    {CONTACT.success.again}
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className='rounded-lg border border-white/10 bg-white/[0.03] p-6 sm:p-9'
        >
            <h2 className='text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#F4F1EA]/45'>
                {CONTACT.formLabel}
            </h2>

            <div className='mt-8 grid gap-6 sm:grid-cols-2'>
                <div>
                    <label htmlFor='name' className={LABEL}>
                        Name
                    </label>

                    <input
                        id='name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        disabled={sending}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                        placeholder='Lucía Amorim'
                        className={cn(FIELD, 'mt-3', errors.name && 'border-[#FF7A66]/70')}
                    />

                    {errors.name && (
                        <p id='name-error' role='alert' className='mt-2 text-[11px] text-[#FF7A66]'>
                            {errors.name}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor='email' className={LABEL}>
                        Email
                    </label>

                    <input
                        id='email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        disabled={sending}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        placeholder='you@studio.com'
                        className={cn(FIELD, 'mt-3', errors.email && 'border-[#FF7A66]/70')}
                    />

                    {errors.email && (
                        <p id='email-error' role='alert' className='mt-2 text-[11px] text-[#FF7A66]'>
                            {errors.email}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor='subject' className={LABEL}>
                        What it is for
                    </label>

                    <div className='relative mt-3'>
                        <select
                            id='subject'
                            name='subject'
                            value={values.subject}
                            onChange={update('subject')}
                            disabled={sending}
                            className={cn(FIELD, 'appearance-none pr-10')}
                        >
                            {CONTACT.subjects.map((option) => (
                                <option key={option} value={option} className='bg-[#0c0c10]'>
                                    {option}
                                </option>
                            ))}
                        </select>

                        <ChevronDown
                            className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-[#F4F1EA]/40'
                            aria-hidden
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor='budget' className={LABEL}>
                        Budget
                    </label>

                    <div className='relative mt-3'>
                        <select
                            id='budget'
                            name='budget'
                            value={values.budget}
                            onChange={update('budget')}
                            disabled={sending}
                            className={cn(FIELD, 'appearance-none pr-10')}
                        >
                            <option value='' className='bg-[#0c0c10]'>
                                Prefer not to say
                            </option>

                            {CONTACT.budgets.map((option) => (
                                <option key={option} value={option} className='bg-[#0c0c10]'>
                                    {option}
                                </option>
                            ))}
                        </select>

                        <ChevronDown
                            className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-[#F4F1EA]/40'
                            aria-hidden
                        />
                    </div>
                </div>

                <div className='sm:col-span-2'>
                    <label htmlFor='message' className={LABEL}>
                        Message
                    </label>

                    <textarea
                        id='message'
                        name='message'
                        rows={6}
                        value={values.message}
                        onChange={update('message')}
                        disabled={sending}
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={errors.message ? 'message-error' : undefined}
                        placeholder='Who is being photographed, what the picture is for, and roughly when.'
                        className={cn(FIELD, 'mt-3 resize-y', errors.message && 'border-[#FF7A66]/70')}
                    />

                    {errors.message && (
                        <p id='message-error' role='alert' className='mt-2 text-[11px] text-[#FF7A66]'>
                            {errors.message}
                        </p>
                    )}
                </div>
            </div>

            {/* Not a real field. Hidden from sight, from the tab order and from
                screen readers, so only a script ever fills it in. */}
            <div className='hidden' aria-hidden>
                <label htmlFor='company'>Company</label>
                <input
                    id='company'
                    name='company'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.company}
                    onChange={update('company')}
                />
            </div>

            <div className='mt-8 flex flex-col gap-4 border-t border-white/10 pt-7 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-[34ch] text-[11px] leading-relaxed text-[#F4F1EA]/45'>
                    {CONTACT.promise} Or write to{' '}
                    <a
                        href={`mailto:${STUDIO_INFO.email}`}
                        className='text-[#F4F1EA]/70 underline underline-offset-4 transition-colors hover:text-[#F4F1EA]'
                    >
                        {STUDIO_INFO.email}
                    </a>
                    .
                </p>

                <button
                    type='submit'
                    disabled={sending}
                    className='inline-flex items-center justify-center gap-2.5 rounded-full bg-[#F4F1EA] px-7 py-3.5 text-[12px] font-medium uppercase tracking-[0.2em] text-[#08080a] transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                >
                    {sending ? (
                        <>
                            <LoaderCircle className='size-4 animate-spin' aria-hidden />
                            Sending
                        </>
                    ) : (
                        <>
                            Send the message
                            <ArrowUpRight className='size-4' aria-hidden />
                        </>
                    )}
                </button>
            </div>

            <p aria-live='polite' className='sr-only'>
                {sending ? 'Sending your message.' : notice}
            </p>

            {notice && status === 'error' && (
                <p role='alert' className='mt-5 rounded-md border border-[#FF7A66]/40 bg-[#FF7A66]/10 px-4 py-3 text-[12px] text-[#FF9E8D]'>
                    {notice}
                </p>
            )}
        </form>
    )
}
